import { eventBus } from '@spon/plugins';
import { getLenis } from '@/globals/SmoothScroll';

const FOCUSABLE = 'a[href], button:not([disabled])';
const PHONE = '(max-width: 600px)';

/**
 * Phone menu. Below 600px the header's own links are hidden and the burger
 * opens #site-menu, a full-screen paper panel (markup: _markup/site/_header).
 * Escape closes, Tab is trapped between the button and the panel links,
 * focus returns to the button, and page scroll (Lenis included) is locked.
 */
function Header({ node }) {
	if (!node) return () => {};

	const button = node.querySelector('.site-menu-toggle');
	const menu = document.getElementById('site-menu');
	if (!button || !menu) return () => {};

	const phone = window.matchMedia(PHONE);
	let isOpen = false;

	const lockScroll = locked => {
		document.documentElement.classList.toggle('is-menu-open', locked);
		const lenis = getLenis();
		if (!lenis) return;
		if (locked) lenis.stop();
		else lenis.start();
	};

	// Everything but the header sits in #page-wrapper, so inert on it keeps
	// the page out of the tab order and the accessibility tree while the
	// panel is open; the header (button + panel) stays live.
	const setPageInert = inert => {
		document
			.querySelectorAll('#page-wrapper, main, footer')
			.forEach(el => el.toggleAttribute('inert', inert));
	};

	const trapItems = () => [button, ...menu.querySelectorAll(FOCUSABLE)];

	const setOpen = (next, { restoreFocus = true } = {}) => {
		if (next === isOpen) return;
		isOpen = next;
		menu.hidden = !next;
		button.setAttribute('aria-expanded', String(next));
		button.setAttribute('aria-label', next ? 'Close menu' : 'Open menu');
		node.classList.toggle('is-menu-open', next);
		lockScroll(next);
		setPageInert(next);

		if (next) menu.querySelector(FOCUSABLE)?.focus();
		else if (restoreFocus) button.focus();
	};

	const onToggle = () => setOpen(!isOpen);

	const onKeyDown = event => {
		if (!isOpen) return;
		if (event.key === 'Escape') {
			event.preventDefault();
			setOpen(false);
			return;
		}
		if (event.key !== 'Tab') return;

		const items = trapItems();
		const first = items[0];
		const last = items[items.length - 1];
		const { activeElement } = document;
		if (event.shiftKey && activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	};

	// Any link inside the panel is a route change (or an anchor / mailto):
	// close without stealing focus from wherever the navigation lands.
	const onMenuClick = event => {
		if (event.target.closest('a')) setOpen(false, { restoreFocus: false });
	};

	// Growing past the phone width with the menu open would strand the lock.
	const onBreakpoint = event => {
		if (!event.matches) setOpen(false, { restoreFocus: false });
	};

	const onPageExit = () => setOpen(false, { restoreFocus: false });

	button.addEventListener('click', onToggle);
	menu.addEventListener('click', onMenuClick);
	document.addEventListener('keydown', onKeyDown);
	phone.addEventListener('change', onBreakpoint);
	eventBus.on('page:exited', onPageExit);

	return () => {
		setOpen(false, { restoreFocus: false });
		button.removeEventListener('click', onToggle);
		menu.removeEventListener('click', onMenuClick);
		document.removeEventListener('keydown', onKeyDown);
		phone.removeEventListener('change', onBreakpoint);
		eventBus.off('page:exited', onPageExit);
	};
}

export default Header;
